<template>
    <ModalBase :show-modal="showModal" @close-modal="close" auto-h class="gk-modal-confirm">
        <template v-slot:titlebar>
            <TitleBar variant-class="title-modal title-modal--simple">
                <template v-slot:title>
                    {{ titleText }}
                </template>
            </TitleBar>
        </template>

        <template v-slot:maincontent>
            <b-row v-if="showAnimation" class="mt-4 animation-and-message-container">
                <b-col v-if="showAnimation === 'success'" cols="3">
                    <AnimationItem class="anim-company" :animation="{ animationData: successAnimation.default, loop: false }" :key="'anim_success'" />
                </b-col>
                <b-col v-if="showAnimation === 'error'" cols="3">
                    <AnimationItem class="anim-company" :animation="{ animationData: errorAnimation.default, loop: false }" :key="'anim_error'" style="max-width: 85px;" />
                </b-col>
                <b-col v-if="showAnimation === 'lock'" cols="3" style="max-height: 94px;">
                    <AnimationItem class="anim-company" :animation="{ animationData: gekkoLockAnimation.default }" :key="'anim_lock'" />
                </b-col>
                <b-col class="d-flex align-items-center justify-content-end" :class="showAnimation === 'error' ? 'mt-2': ''">
                    <div class="target-item message-container">
                        {{ messageText }}
                    </div>
                </b-col>
            </b-row>
            <div v-if="!showAnimation" class="target-item message-container mt-4">
                {{ messageText }}
            </div>
            <div v-if="actionType === 'confirmation'">
                <div v-if="confirmationStep === 'search-company'">
                    <div class="mb-3">
                        <b-form-group
                            :label="$gettext('Search company name')"
                            label-for="company-search"
                            label-class="font-weight-bold"
                        >
                            <b-form-input
                                id="company-search"
                                v-model="searchQuery"
                                @input="onSearchInput"
                                :placeholder="$gettext('Enter company name...')"
                                type="text"
                            ></b-form-input>
                            <b-tooltip 
                                target="company-search" 
                                :show.sync="showMinLengthTooltip" 
                                placement="top"
                                triggers=""
                            >
                                {{ $gettext('Please enter at least 4 characters to search') }}
                            </b-tooltip>
                        </b-form-group>
                    </div>
                    <div v-if="loadingCompanies" class="text-center mt-5 mb-3">
                        <b-spinner variant="primary" label="Loading..."></b-spinner>
                        <p class="mt-2">{{ $gettext('Searching companies...') }}</p>
                    </div>
                    <div v-else-if="searchedCompanyList.length > 0" class="company-list-container mt-3">
                        <div class="table-responsive">
                            <table class="table table-hover">
                                <thead>
                                    <tr>
                                        <th style="width: 35%;">{{ $gettext('Company name') }}</th>
                                        <th style="width: 30%;">{{ $gettext('Business ID') }}</th>
                                        <th style="width: 15%;">{{ $gettext('ID Type') }}</th>
                                    </tr>
                                </thead>
                                <tbody class="scrollable-tbody">
                                    <tr v-for="(company, index) in searchedCompanyList" :key="index" @click="selectCompany(company)" class="company-row">
                                        <td style="width: 45%;" class="company-table-cell">{{ company.name }}</td>
                                        <td style="width: 40%;" class="company-table-cell">{{ company.eia }}</td>
                                        <td style="width: 15%;">{{ company.eaid_name.split(':')[1] || company.eaid_name }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div v-else-if="searchedCompanyList.length === 0 && searchQuery.trim().length > 3 && !showOptionsAlert && !debouncing">
                        <i>{{ $gettext('No companies were found with the given company name') }}</i>
                    </div>
                    <div v-if="showOptionsAlert" class="alert alert-warning mt-3">
                        {{ $gettext('Your search returned too many results. Please refine your search with more specific terms.') }}
                    </div>
                </div>
                
                <div v-if="confirmationStep === 'set-project-reference'">
                    <b-form-input
                        id="project-reference"
                        v-model="invoiceToUpdate.project_reference"
                        :placeholder="$gettext('E.g. PO-12345')"
                        type="text"
                    ></b-form-input>
                </div>
            </div>
            <p v-if="actionType === 'redirect_invoice'" class="text-danger pull-right mt-4 mr-4">
                {{ $gettext('Redirecting to invoice edit page...') }}
            </p>
        </template>

        <template v-slot:actions>
            <GekkoButton
                v-if="actionType === 'edit_invoice'"
                :to="editUrl"
                @click="close"
                :text="$gettext('Edit invoice')"
                variant="link" />
            <GekkoButton
                v-if="actionType === 'upload_certificate'"
                :href="Urls['accounts_verifactu_settings']()"
                :text="$gettext('Upload certificate')"
                variant="link" />
            <GekkoButton
                v-if="actionType === 'connect_peppol'"
                :href="Urls['accounts_peppol_settings']()"
                :text="$gettext('Connect to Peppol')"
                variant="link" />
            <GekkoButton
                v-if="actionType === 'edit_company_profile'"
                :href="Urls['accounts_manage_companies']()"
                :text="$gettext('Edit company profile')"
                variant="link" />
            <GekkoButton
                v-if="!settingsPage && actionType === 'verifactu_settings'"
                :href="Urls['accounts_verifactu_settings']()"
                :text="$gettext('Upload certificate')"
                variant="link" />
            <GekkoButton
                v-if="actionType === 'confirmation'"
                @click="close"
                :text="$gettext('Close')"
                class="text-danger"
                variant="link" />
            <GekkoButton
                v-if="actionType === 'confirmation' && confirmationStep === 'search-company'"
                @click="selectCompany(null)"
                :text="$gettext('Continue')" />
            <GekkoButton
                v-if="actionType === 'confirmation' && confirmationStep === 'set-project-reference'"
                @click="confirmProjectReference"
                :text="$gettext('Done')" />
        </template>
    </ModalBase>
</template>

<script>
import InvoiceService from "@/store/services/invoice.service"
import ModalBase from "@/components/base_components/ModalBase"
import GekkoButton from "@/components/GekkoButton"
import TitleBar from "@/components/TitleBar"
import AnimationItem from '@/components/dashboard/ui/AnimationItem.vue'
import * as successAnimation from '@/css/animations/success-check.json'
import * as errorAnimation from '@/css/animations/failure-cross.json'
import * as gekkoLockAnimation from '@/css/animations/gekko-lock.json'

export default {
    name: "EInvoiceMessageModal",
    components: {
        ModalBase,
        GekkoButton,
        TitleBar,
        AnimationItem
    },
    props: {
        showModal: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            required: true
        },
        message: {
            type: String,
            required: true
        },
        actionType: {
            type: String,
            default: null
        },
        recurrentId: {
            type: Number,
            default: null
        },
        showAnimation: {
            type: String,
            default: null
        },
        settingsPage: {
            type: Boolean,
            default: false
        },
        searchedCompanies: {
            type: Array,
            default: () => []
        }
    },
    watch: {
        title(newVal) {
            this.titleText = newVal
        },
        message(newVal) {
            this.messageText = newVal
        },
        searchedCompanies(newVal) {
            this.searchedCompanyList = newVal
        }
    },
    data() {
        return {
            titleText: '',
            messageText: '',
            successAnimation: successAnimation,
            errorAnimation: errorAnimation,
            gekkoLockAnimation: gekkoLockAnimation,
            searchedCompanyList: [],
            searchQuery: '',
            debounceTimer: null,
            debouoncing: false,
            loadingCompanies: false,
            showMinLengthTooltip: false,
            tooltipTimer: null,
            showOptionsAlert: false,
            selectedCompany: null,
            confirmationStep: 'search-company', // other possible value: 'set-project-reference'
            invoiceToUpdate: {
                project_reference: null
            },
            searchRequestId: 0
        }
    },
    computed: {
        editUrl() {
            if (this.recurrentId) {
                return { name: 'InvoiceViewRecurrent', params: { id: this.recurrentId } }
            } else {
                return { name: 'InvoiceEdit', params: { id: this.$route.params['invoiceId']} }
            }
        }
    },
    methods: {
        resetData() {
            this.searchQuery = ''
            this.confirmationStep = 'search-company'
            this.selectedCompany = null
            this.invoiceToUpdate = {
                project_reference: null
            }
            this.searchedCompanyList = []
            this.showOptionsAlert = false
        },
        close() {
            this.$emit("close")
            this.resetData()
        },
        selectCompany(company) {
            this.selectedCompany = company
            this.confirmationStep = 'set-project-reference'
            this.titleText = this.$gettext('Project reference')
            this.messageText = this.$gettext('Please confirm that the project reference for your e-invoice is correct.')

            const invoiceId = this.$route.params['invoiceId']
            InvoiceService.retrieve(invoiceId).then((response) => {
                this.invoiceToUpdate = response.data
            }).catch(() => {
                this.$store.commit('setAlert', {
                    title: this.$gettext("Error retrieving invoice data"),
                    text: this.$gettext("We could not find the invoice project reference to proceed with the e-invoice sending."),
                    variant:"danger",
                    duration: 4
                })
            })
        },
        confirmProjectReference() {
            if (!this.invoiceToUpdate.project_reference) {
                this.$store.commit('setAlert', {
                    title: this.$gettext("Project reference required"),
                    text: this.$gettext("You must provide a project reference before sending the e-invoice via Peppol."),
                    variant:"danger",
                    duration: 4
                })
                return
            }

            const invoiceId = this.$route.params['invoiceId']
            InvoiceService.update(invoiceId, this.invoiceToUpdate)
                .then(() => {
                    this.$emit("selectedCompany", this.selectedCompany)
                    this.resetData()
                })
                .catch(() => {
                    this.$store.commit('setAlert', {
                        title: this.$gettext("Error updating project reference"),
                        text: this.$gettext("An error occurred while updating the project reference, and the e-invoice could not be sent."),
                        variant:"danger",
                        duration: 4
                    })
                })
        },
        onSearchInput() {
            this.debouncing = true
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer)
            }
            
            this.debounceTimer = setTimeout(() => {
                this.debouncing = false
                this.searchPeppolCompanyByName()
            }, 500)
        },
        searchPeppolCompanyByName() {
            if (!this.searchQuery || this.searchQuery.trim().length === 0) {
                return
            } else if (this.searchQuery.trim().length <= 3) {
                this.showTooltip()
                return
            }
            
            // Increment request ID to invalidate previous requests
            this.searchRequestId++
            const currentRequestId = this.searchRequestId
            
            const invoiceId = this.$route.params['invoiceId']
            this.loadingCompanies = true
            InvoiceService.searchPeppolCompanyByName(invoiceId, { company_name: this.searchQuery })
                .then((response) => {
                    // Only process if this is still the latest request
                    if (currentRequestId !== this.searchRequestId) {
                        return
                    }
                    
                    this.searchedCompanyList = response.data.map(company => {
                        return {
                            eaid_name: company.identifier ? company.identifier.eaid_name : '',
                            eaid_code: company.identifier ? company.identifier.eaid_code : '',
                            endpoint_id: company.identifier ? company.identifier.endpoint_id : '',
                            eia: company.eia,
                            name: company.participant.name
                        }
                    })
                    this.showOptionsAlert = false
                })
                .catch((error) => {
                    // Only process if this is still the latest request
                    if (currentRequestId !== this.searchRequestId) {
                        return
                    }
                    
                    if (error.status === 404) {
                        this.searchedCompanyList = []
                        this.showOptionsAlert = false
                    } else if (error.status === 413) {
                        this.searchedCompanyList = []
                        this.showOptionsAlert = true
                    } else {
                        this.$store.commit('setAlert', {
                            title: this.$gettext("Error searching companies"),
                            text: this.$gettext("Something went wrong while searching for companies in the Peppol network."),
                            variant:"danger",
                            duration: 4
                        })
                    }
                })
                .finally(() => {
                    // Only update loading state if this is still the latest request
                    if (currentRequestId === this.searchRequestId) {
                        this.loadingCompanies = false
                    }
                })
        },
        showTooltip() {
            if (this.tooltipTimer) {
                clearTimeout(this.tooltipTimer)
            }
            
            this.showMinLengthTooltip = true
            
            this.tooltipTimer = setTimeout(() => {
                this.showMinLengthTooltip = false
            }, 3000)
        }
    }
}
</script>

<style scoped>
.message-container {
    white-space: pre-line;
    word-break: break-word;
    overflow-wrap: break-word;
}

.company-list-container {
    overflow-y: auto;
}

.company-list-container .table {
    margin-bottom: 0;
}

.scrollable-tbody {
    display: block;
    max-height: 250px;
    overflow-y: auto;
}

.scrollable-tbody tr {
    display: table;
    width: 100%;
    table-layout: fixed;
}

.company-list-container thead {
    display: table;
    width: 100%;
    table-layout: fixed;
}

.company-row {
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.company-row:hover {
    background-color: #f8f9fa !important;
}

.company-row:hover td {
    background-color: #e9ecef !important;
}

.company-table-cell {
    max-width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.animation-and-message-container {
    display: flex;
    align-items: center;
    justify-content: space-around;
    display: flex;
}
</style>